import { css } from '@emotion/react';
import {
  CallRecordingStatus,
  HydratedCallRecord,
} from '@weave/schema-gen-ts/dist/schemas/phone-exp/phone-records/v1/call_record.pb';
import { getCallRecording } from '@frontend/api-phone-calls';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { WithTooltip } from '@frontend/phone-ui';
import { CachedAudioScrubber } from '@frontend/phone-ui-widgets';
import { theme } from '@frontend/theme';

type Props = {
  call: HydratedCallRecord;
  callRecordingPath: string;
  hasAccessToCallRecording: boolean;
  isDemoAccount?: boolean;
  autoplayRecording?: boolean;
};

const CallRecordingNoAccess = ({ status }: { status: CallRecordingStatus }) => {
  const { t } = useTranslation('call-insight-panel');
  const tooltipText =
    status === CallRecordingStatus.CALL_RECORDING_STATUS_RECORDING_INACCESSIBLE_PRIVATE_NUMBER
      ? t(
          'You do not have permission to access call recordings related to this office number. Contact your system administrator for access.'
        )
      : t(
          'You do not have permission to access call recordings for this location. Contact your system administrator for access.'
        );

  return (
    <WithTooltip tooltipText={tooltipText}>
      <span css={styles.status}>
        <Icon name='lock' size={20} color='light' />
        {t('No access to Call Recording')}
      </span>
    </WithTooltip>
  );
};

export const CallRecordingSection = ({
  call,
  callRecordingPath,
  hasAccessToCallRecording,
  isDemoAccount,
  autoplayRecording,
}: Props) => {
  const { t } = useTranslation('call-insight-panel');
  // Demo records are generated in the frontend and may not include the backend recording status.
  const callRecordingStatus =
    isDemoAccount && callRecordingPath
      ? CallRecordingStatus.CALL_RECORDING_STATUS_RECORDING_AVAILABLE
      : call.callRecordingStatus;

  if (
    !hasAccessToCallRecording &&
    callRecordingStatus !== CallRecordingStatus.CALL_RECORDING_STATUS_RECORDING_INACCESSIBLE_PRIVATE_NUMBER
  ) {
    return (
      <div data-channel-id={call.channelId ?? ''}>
        <CallRecordingNoAccess status={CallRecordingStatus.CALL_RECORDING_STATUS_RECORDING_INACCESSIBLE_MISSING_ACL} />
      </div>
    );
  }

  switch (callRecordingStatus) {
    case CallRecordingStatus.CALL_RECORDING_STATUS_RECORDING_DELETED:
      return (
        <div data-channel-id={call.channelId ?? ''}>
          <span css={styles.status}>
            <Icon name='trash-small' size={20} color='light' />
            {t('Call Recording deleted')}
          </span>
        </div>
      );
    case CallRecordingStatus.CALL_RECORDING_STATUS_RECORDING_INACCESSIBLE_MISSING_ACL:
      return (
        <div data-channel-id={call.channelId ?? ''}>
          <CallRecordingNoAccess
            status={CallRecordingStatus.CALL_RECORDING_STATUS_RECORDING_INACCESSIBLE_MISSING_ACL}
          />
        </div>
      );
    case CallRecordingStatus.CALL_RECORDING_STATUS_RECORDING_INACCESSIBLE_PRIVATE_NUMBER:
      return (
        <div data-channel-id={call.channelId ?? ''}>
          <CallRecordingNoAccess
            status={CallRecordingStatus.CALL_RECORDING_STATUS_RECORDING_INACCESSIBLE_PRIVATE_NUMBER}
          />
        </div>
      );
    case CallRecordingStatus.CALL_RECORDING_STATUS_RECORDING_AVAILABLE:
      break;
    case CallRecordingStatus.CALL_RECORDING_STATUS_NOT_RECORDED:
    case CallRecordingStatus.CALL_RECORDING_STATUS_UNSPECIFIED:
    default:
      return null;
  }

  return (
    <div data-channel-id={call?.channelId ?? ''}>
      <CachedAudioScrubber
        singlePlayer
        persistPlaybackRate
        trackingId='nwx-phones-call-insight-call-recording-audioScrubber'
        filePath={callRecordingPath}
        autoplay={autoplayRecording}
        queryFn={
          callRecordingPath
            ? undefined
            : async () => {
                try {
                  if (!call.channelId || !call.locationId) {
                    throw new Error('Missing channelId or locationId');
                  }

                  const res = await getCallRecording({
                    channelId: call.channelId,
                    locationId: call.locationId,
                    // Token is optional per GetCallRecordingSignedUrlRequest schema.
                    // The token is only returned by GetHydratedCallRecord when the recording is already
                    // processed. If a real-time update adds a new call record to the table, the subsequent
                    // GetHydratedCallRecord fetch may occur before the recording is processed, so no token
                    // is returned, and no re-fetch is made to obtain it later.
                    // The backend authenticates via the Bearer header, so the token is not required.
                    token: call.downloadCallRecordingToken,
                  });
                  if (res.signedUrl) {
                    return fetch(res.signedUrl);
                  } else {
                    throw new Error('No signed URL available');
                  }
                } catch (error) {
                  console.error('Error fetching call recording:', error);
                  throw error;
                }
              }
        }
        mediaId={call?.channelId ? `call-recording-${call.channelId}` : ''}
      />
    </div>
  );
};

const styles = {
  status: css`
    display: grid;
    grid-template-columns: ${theme.spacing(5)} auto;
    align-items: center;

    > svg {
      justify-self: center;
    }
  `,
};
